import React, { useEffect } from "react";
import { useMatch, useNavigate } from "react-router";
import { toast } from "react-toastify/unstyled";
import { Toast } from "@sscs/ui";
import { useAppDispatch, useAppSelector } from "Hooks";
import {
  NotificationStatePage,
  UnsavedChange as UnsavedChangeType,
  selectAllUnsavedChanges,
  selectHasUnsavedChanges,
  updateRemindMe,
} from "../store/notifications";
import { getToastNotificationId } from "../utils";

const handleUnload = (event: BeforeUnloadEvent) => {
  // bear in mind most browsers no longer support a custom message so this will only be shown in the few that still do
  const message =
    "You have unsaved changes. Are you sure you want to continue?"; //Gecko + IE;
  event.preventDefault();
  (event || window.event).returnValue = message;
  return message;
};

type UnsavedChangesProps = {
  id: string;
  page: NotificationStatePage;
  unsavedChange: UnsavedChangeType;
};

const UnsavedChange = ({
  id,
  page,
  unsavedChange,
}: UnsavedChangesProps): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const matchesRoute = Boolean(
    useMatch({
      path: unsavedChange.matchingRoute ?? unsavedChange.navigateTo,
      end: !unsavedChange.matchingRoute,
    }),
  );

  useEffect(() => {
    const toastId = getToastNotificationId(page, id);

    if (!matchesRoute && unsavedChange.remindMe) {
      // One pending change is one reminder: this effect re-runs on every
      // navigation, and toastify appends rather than replaces a repeated id.
      if (toast.isActive(toastId)) return;

      Toast.show({
        title: unsavedChange.text,
        actions: [
          {
            label: "Show me",
            onClick: () => navigate(unsavedChange.navigateTo),
          },
          {
            label: "Don't remind me",
            variant: "secondary",
            onClick: () =>
              dispatch(updateRemindMe({ id, page, remindMe: false })),
          },
        ],
        type: "warning",
        props: {
          toastId: toastId,
          autoClose: false,
          closeButton: false,
          closeOnClick: false,
        },
      });
    } else {
      toast.dismiss(toastId);
    }
  }, [id, page, unsavedChange, matchesRoute, dispatch, navigate]);

  return <></>;
};

const UnsavedChanges = (): React.JSX.Element => {
  const unsavedChanges = useAppSelector(selectAllUnsavedChanges);
  const hasUnsavedChanges = useAppSelector(selectHasUnsavedChanges);

  // handle the browser prompt when user reloads or closes tab/window
  useEffect(() => {
    if (hasUnsavedChanges)
      window.addEventListener("beforeunload", handleUnload);
    else window.removeEventListener("beforeunload", handleUnload);

    // remove on unmount
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, [unsavedChanges, hasUnsavedChanges]);

  return (
    <>
      {Object.entries(unsavedChanges).map(
        ([page, unsavedChangesPerPage]: [
          NotificationStatePage,
          Record<string, UnsavedChangeType>,
        ]) => (
          <React.Fragment key={page}>
            {Object.entries(unsavedChangesPerPage).map(
              ([id, unsavedChange]) => (
                <UnsavedChange
                  key={getToastNotificationId(page, id)}
                  id={id}
                  page={page}
                  unsavedChange={unsavedChange}
                />
              ),
            )}
          </React.Fragment>
        ),
      )}
    </>
  );
};

export default UnsavedChanges;
